import { Avatar, CloseButton, Modal } from '@pa/ui'
import { useId, useState } from 'react'
import type { Provider } from '../model/types'

/** The mockup's avatar palette (`components/ui.tsx` `AVATAR_COLORS`): the C-08 colour choices (Q-2). */
export const PROVIDER_COLORS = ['#3b67ac', '#252f61', '#3a9e9b', '#ec6b31', '#429bdf', '#7a5cc4', '#c2577a'] as const

/**
 * [New] C-08 "Details PF doesn't provide" (Q-2; Frontend §6.3): specialty (≤ 80), a short bio (≤ 500) and the
 * avatar colour, shown to patients. Part of the Settings draft; Refresh from PF never overwrites them.
 */
export function ProviderDetailsModal({
  provider,
  onClose,
  onDone,
}: {
  provider: Provider
  onClose: () => void
  onDone: (details: Pick<Provider, 'specialty' | 'bio' | 'color'>) => void
}) {
  const titleId = useId()
  const [specialty, setSpecialty] = useState(provider.specialty)
  const [bio, setBio] = useState(provider.bio)
  const [color, setColor] = useState(provider.color)
  const colors: string[] = PROVIDER_COLORS.includes(provider.color as (typeof PROVIDER_COLORS)[number])
    ? [...PROVIDER_COLORS]
    : [provider.color, ...PROVIDER_COLORS]
  return (
    <Modal onClose={onClose} labelledBy={titleId}>
      <div className="modal-head">
        <div className="row provider-details-head">
          <Avatar name={provider.name} color={color} />
          <div>
            <h2 id={titleId}>{provider.name}</h2>
            <p className="muted">Shown to patients when they pick a provider.</p>
          </div>
        </div>
        <CloseButton onClick={onClose} />
      </div>
      <div className="modal-body set-form">
        <label className="field">
          <span className="label">Specialty</span>
          <input
            className="input"
            value={specialty}
            maxLength={80}
            placeholder="e.g. Family medicine"
            onChange={(e) => {
              setSpecialty(e.target.value)
            }}
          />
        </label>
        <label className="field">
          <span className="label">Bio</span>
          <textarea
            className="input q2-input"
            value={bio}
            maxLength={500}
            rows={4}
            onChange={(e) => {
              setBio(e.target.value)
            }}
          />
        </label>
        <div className="field">
          <span className="label" id={`${titleId}-color`}>
            Colour
          </span>
          <div className="swatches" role="radiogroup" aria-labelledby={`${titleId}-color`}>
            {colors.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={c === color}
                aria-label={c}
                className="swatch"
                style={{ background: c }}
                onClick={() => {
                  setColor(c)
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="modal-foot">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            onDone({ specialty: specialty.trim(), bio: bio.trim(), color })
          }}
        >
          Done
        </button>
      </div>
    </Modal>
  )
}
